import type { ReloginIntent } from "@/types";

const RELOGIN_COOKIE = "telekomid_relogin";
const RELOGIN_TTL_SECONDS = 120;

const getCookieDomain = () => {
  const host = window.location.hostname.toLowerCase();
  if (host === "localhost") return null;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return null;

  const parts = host.split(".").filter(Boolean);
  if (parts.length < 2) return null;

  return "." + parts.slice(-2).join(".");
};

const setCookie = (name: string, value: string, maxAgeSeconds: number) => {
  const domain = getCookieDomain();
  if (!domain) return false;

  document.cookie = [
    `${name}=${encodeURIComponent(value)}`,
    `Max-Age=${maxAgeSeconds}`,
    "Path=/",
    `Domain=${domain}`,
    "Secure",
    "SameSite=Lax",
  ].join("; ");

  return true;
};

const getCookie = (name: string) => {
  const escaped = name.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
  const m = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
};

export const clearCookie = (name: string) => {
  const domain = getCookieDomain();
  if (!domain) return;

  document.cookie = [
    `${name}=`,
    "Max-Age=0",
    "Path=/",
    `Domain=${domain}`,
    "Secure",
    "SameSite=Lax",
  ].join("; ");
};

export const setReloginIntent = (channel: string) => {
  const payload: ReloginIntent = {
    v: 1,
    type: "relogin",
    channel,
    ts: Date.now(),
  };
  return setCookie(
    RELOGIN_COOKIE,
    JSON.stringify(payload),
    RELOGIN_TTL_SECONDS,
  );
};

export const peekReloginIntent = (): ReloginIntent | null => {
  const raw = getCookie(RELOGIN_COOKIE);
  if (!raw) return null;

  try {
    const data = JSON.parse(raw) as Partial<ReloginIntent>;
    if (data.type !== "relogin") return null;
    if (typeof data.ts !== "number") return null;
    if (Date.now() - data.ts > RELOGIN_TTL_SECONDS * 1000) return null;
    return data as ReloginIntent;
  } catch {
    return null;
  }
};

export const consumeReloginIntent = () => clearCookie(RELOGIN_COOKIE);
